import React, { useState, useEffect } from 'react';
import { 
  Typography, Card, Form, Input, InputNumber, Button, Select, Upload, 
  message, Space, Row, Col, Tag, Progress, Alert, Tooltip, Modal, Spin, Avatar 
} from 'antd';
import { 
  DollarCircleOutlined, UploadOutlined, CheckCircleOutlined, UserOutlined, 
  BankOutlined, FileTextOutlined, InfoCircleOutlined, InboxOutlined, 
  DeleteOutlined, EyeOutlined, SyncOutlined, CalendarOutlined, PhoneOutlined, 
  SafetyCertificateOutlined, CreditCardOutlined, ArrowLeftOutlined, 
  ThunderboltOutlined, CheckOutlined, FileImageOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import PersianDatePicker from '../../components/PersianDatePicker';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { toJalali, formatDate } from '../../utils/dateUtils';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

const toLatinDigits = (value) => {
  if (!value) return value;
  return String(value).replace(/[۰-۹]/g, w => String.fromCharCode(w.charCodeAt(0) - 1728));
};

const amountToPersianWords = (n) => {
  if (!n || isNaN(n)) return '';
  n = Math.floor(Number(n));
  if (n === 0) return 'صفر تومان';
  const units = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
  const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
  const tens = ['', 'ده', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
  const hundreds = ['', 'یکصد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
  const scales = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'];

  function chunkToWords(num) {
    let parts = [];
    const h = Math.floor(num / 100);
    const rem = num % 100;
    if (h > 0) parts.push(hundreds[h]);
    if (rem >= 10 && rem <= 19) {
      parts.push(teens[rem - 10]);
    } else {
      const t = Math.floor(rem / 10);
      const u = rem % 10;
      if (t > 0) parts.push(tens[t]);
      if (u > 0) parts.push(units[u]);
    }
    return parts.join(' و ');
  }

  let chunks = [];
  let temp = n;
  while (temp > 0) {
    chunks.push(temp % 1000);
    temp = Math.floor(temp / 1000);
  }

  let resParts = [];
  for (let i = chunks.length - 1; i >= 0; i--) {
    const chunk = chunks[i];
    if (chunk > 0) {
      const words = chunkToWords(chunk);
      const scale = scales[i];
      resParts.push(scale ? words + ' ' + scale : words);
    }
  }
  return resParts.join(' و ') + ' تومان';
};

const FINANCIAL_STATUS_CONFIG = {
  unpaid: { text: 'تسویه‌نشده', color: 'error' },
  partial: { text: 'تسویه جزئی', color: 'warning' },
  settled: { text: 'تسویه کامل', color: 'success' },
};

const RegisterDealPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [deals, setDeals] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [enteredAmount, setEnteredAmount] = useState(null);

  // Parse URL query parameter if deal_id is provided
  const queryParams = new URLSearchParams(location.search);
  const initialDealId = queryParams.get('deal_id');

  useEffect(() => {
    const loadFormData = async () => {
      try {
        setFetchingData(true);
        const res = await financialApi.getDealsForPayment();
        if (res?.data) {
          const loadedDeals = res.data.deals || [];
          const loadedBanks = res.data.bank_accounts || [];
          setDeals(loadedDeals);
          setBankAccounts(loadedBanks);

          if (initialDealId) {
            const found = loadedDeals.find(d => String(d.id) === String(initialDealId));
            if (found) {
              handleSelectDeal(found.id, loadedDeals);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load deals and bank accounts', err);
        message.error('خطا در دریافت لیست معاملات و حساب‌های بانکی');
      } finally {
        setFetchingData(false);
      }
    };

    loadFormData();
  }, [initialDealId]);

  const handleSelectDeal = (dealId, currentDeals = deals) => {
    const deal = currentDeals.find(d => d.id === dealId);
    setSelectedDeal(deal || null);
    form.setFieldsValue({ deal_id: dealId, deal_installment_id: undefined });

    if (deal) {
      // Suggest remaining amount if unpaid
      if (deal.remaining_amount > 0) {
        form.setFieldsValue({ amount: deal.remaining_amount });
        setEnteredAmount(deal.remaining_amount);
      }
      // If deal has specific default purpose
      if (deal.remaining_amount <= 0) {
        form.setFieldsValue({ payment_purpose: 'settlement' });
      } else if (deal.paid_amount > 0) {
        form.setFieldsValue({ payment_purpose: 'installment' });
      } else {
        form.setFieldsValue({ payment_purpose: 'down_payment' });
      }
    }
  };

  const handleInstallmentChange = (instId) => {
    if (!selectedDeal || !selectedDeal.installments) return;
    const inst = selectedDeal.installments.find(i => i.id === instId);
    if (inst) {
      form.setFieldsValue({
        amount: inst.amount,
        payment_purpose: 'installment',
      });
      setEnteredAmount(inst.amount);
    }
  };

  const handleFillRemainingAmount = () => {
    if (selectedDeal && selectedDeal.remaining_amount > 0) {
      form.setFieldsValue({ amount: selectedDeal.remaining_amount });
      setEnteredAmount(selectedDeal.remaining_amount);
      message.info(`مبلغ به اندازه کل مانده معامله (${formatPriceToman(selectedDeal.remaining_amount)}) تنظیم شد.`);
    }
  };

  const handleFinish = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('amount', Number(toLatinDigits(values.amount)));
      if (values.sales_channel) formData.append('sales_channel', values.sales_channel);
      formData.append('payment_method_type', values.payment_method_type);
      if (values.tracking_code) formData.append('tracking_code', toLatinDigits(values.tracking_code).trim());
      if (values.payment_purpose) formData.append('payment_purpose', values.payment_purpose);
      if (values.bank_account_id) formData.append('bank_account_id', Number(values.bank_account_id));
      if (values.paid_on) {
        let rawDate = values.paid_on;
        if (typeof rawDate === 'object' && rawDate?.format) {
          rawDate = rawDate.format('YYYY/MM/DD');
        } else if (rawDate instanceof Date) {
          rawDate = toJalali(rawDate);
        }
        formData.append('paid_on', toLatinDigits(rawDate));
      }
      if (values.remarks) formData.append('remarks', values.remarks);
      if (values.next_installment_amount && values.next_installment_date) {
        let nd = values.next_installment_date;
        if (typeof nd === 'object' && nd?.format) {
          nd = nd.format('YYYY/MM/DD');
        } else if (nd instanceof Date) {
          nd = toJalali(nd);
        }
        formData.append('next_installment_amount', Number(toLatinDigits(values.next_installment_amount)));
        formData.append('next_installment_date', toLatinDigits(nd));
      }
      if (values.deal_installment_id) formData.append('deal_installment_id', values.deal_installment_id);

      if (fileList.length > 0) {
        formData.append('receipt_file', fileList[0].originFileObj || fileList[0]);
      }

      await financialApi.registerDealPayment(Number(values.deal_id), formData);
      message.success('واریزی با موفقیت ثبت شد و به کارتابل مدیر مالی جهت تأیید ارسال گردید.');
      
      form.resetFields();
      setSelectedDeal(null);
      setFileList([]);
      setEnteredAmount(null);
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ثبت واریزی معامله');
    } finally {
      setLoading(false);
    }
  };

  const handleBeforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (isImage) {
      file.preview = URL.createObjectURL(file);
    }
    setFileList([file]);
    return false; // Prevent auto upload
  };

  const handleRemoveFile = () => {
    setFileList([]);
  };

  const handlePreview = (file) => {
    if (file.preview || file.url) {
      setPreviewImage(file.preview || file.url);
      setPreviewVisible(true);
    } else {
      message.info('پیش‌نمایش برای این نوع فایل در دسترس نیست');
    }
  };

  return (
    <MainLayout>
      <div className="p-4 md:p-6 pb-28 max-w-[1600px] mx-auto animate-fade-in" style={{ fontFamily: 'CustomFont' }}>
        
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-2xl shadow-sm border border-emerald-100 dark:border-emerald-800">
              <DollarCircleOutlined />
            </div>
            <div>
              <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-extrabold flex items-center gap-2">
                <span>ثبت واریزی جدید معامله</span>
                <Tag color="cyan" className="text-xs px-2 py-0.5 rounded-full font-medium">
                  کارتابل تأیید واریز
                </Tag>
              </Title>
              <Text className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">
                ثبت اطلاعات پرداخت مشتری، تسویه اقساط و بارگذاری فیش بانکی جهت تأیید مدیر مالی
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/finance/approvals')}
              className="text-xs"
            >
              کارتابل تأیید پرداخت‌ها
            </Button>
          </div>
        </div>

        {fetchingData ? (
          <div className="py-24 text-center">
            <Spin size="large" tip="در حال بارگذاری اطلاعات معاملات و حساب‌های بانکی..." />
          </div>
        ) : (
          <Row gutter={[24, 24]}>
            {/* Form Column (Right) */}
            <Col xs={24} lg={15} xl={16}>
              <Form 
                form={form} 
                layout="vertical" 
                onFinish={handleFinish}
                initialValues={{
                  sales_channel: 'phone',
                  payment_method_type: 'card_to_card',
                  payment_purpose: 'invoice',
                }}
              >
                {/* 1. Deal & Customer Selection Card */}
                <Card 
                  bordered={false} 
                  className="shadow-sm rounded-2xl mb-5 border border-gray-100 dark:border-gray-800 dark:bg-gray-800"
                  title={
                    <span className="font-bold text-sm text-gray-800 dark:text-gray-100 flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
                      <UserOutlined className="text-blue-500" />
                      انتخاب معامله و مشتری طرف حساب
                    </span>
                  }
                >
                  <Form.Item 
                    name="deal_id" 
                    label="معامله مرتبط" 
                    rules={[{ required: true, message: 'لطفاً معامله مورد نظر را انتخاب نمایید' }]}
                    help="می‌توانید بر اساس عنوان معامله، نام مشتری، شماره موبایل یا شناسه معامله جستجو کنید."
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="جستجو و انتخاب معامله (نام معامله، نام یا موبایل مشتری)..."
                      onChange={(val) => handleSelectDeal(val)}
                      filterOption={(input, option) => {
                        if (!input) return true;
                        const normInput = toLatinDigits(input).toLowerCase().trim();
                        const normKey = toLatinDigits(option?.searchKey || '').toLowerCase();
                        return normKey.includes(normInput);
                      }}
                      className="w-full"
                      size="large"
                    >
                      {deals.map(d => {
                        const clientName = d.client?.name || 'مشتری نامشخص';
                        const clientMobile = d.client?.mobile ? toLatinDigits(d.client.mobile) : '';
                        const searchKey = `${d.id} ${d.name} ${clientName} ${clientMobile}`;
                        const statusConfig = FINANCIAL_STATUS_CONFIG[d.financial_status] || { text: 'نامشخص', color: 'default' };

                        return (
                          <Option 
                            key={d.id} 
                            value={d.id} 
                            searchKey={searchKey}
                          >
                            <div className="flex items-center justify-between py-1" style={{ fontFamily: 'CustomFont' }}>
                              <div className="flex flex-col">
                                <span className="font-bold text-gray-800 dark:text-gray-200">
                                  {d.name} <span className="text-xs text-gray-400 font-mono">#{d.id}</span>
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  مشتری: {clientName} {clientMobile ? `(${clientMobile})` : ''}
                                </span>
                              </div>
                              <div className="text-left flex items-center gap-2">
                                <span className="text-xs font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                                  {formatPriceToman(d.value)}
                                </span>
                                <Tag color={statusConfig.color} className="text-[11px] m-0 px-1.5 py-0">
                                  {statusConfig.text}
                                </Tag>
                              </div>
                            </div>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>

                  {/* Optional Installment Picker */}
                  {selectedDeal && selectedDeal.installments && selectedDeal.installments.length > 0 && (
                    <Form.Item 
                      name="deal_installment_id" 
                      label="قسط متصل معامله (اختیاری)"
                      help="با انتخاب قسط، مبلغ آن به‌صورت خودکار در فیلد مبلغ واریزی درج خواهد شد."
                    >
                      <Select 
                        allowClear 
                        placeholder="انتخاب قسط معامله جهت تسویه..."
                        onChange={handleInstallmentChange}
                      >
                        {selectedDeal.installments.map(inst => (
                          <Option key={inst.id} value={inst.id}>
                            قسط شماره {inst.installment_number} - مبلغ: {formatPriceToman(inst.amount)} 
                            {inst.due_date ? ` (سررسید: ${toJalali(inst.due_date)})` : ''}
                            {inst.status === 'paid' ? ' [تسویه شده]' : ''}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </Card>

                {/* 2. Payment & Financial Details Card */}
                <Card 
                  bordered={false} 
                  className="shadow-sm rounded-2xl mb-5 border border-gray-100 dark:border-gray-800 dark:bg-gray-800"
                  title={
                    <span className="font-bold text-sm text-gray-800 dark:text-gray-100 flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
                      <CreditCardOutlined className="text-emerald-500" />
                      مشخصات مالی و حساب‌های واریز
                    </span>
                  }
                >
                  <Row gutter={16}>
                    {/* Amount */}
                    <Col xs={24} md={12}>
                      <Form.Item 
                        name="amount" 
                        label={
                          <div className="flex items-center justify-between w-full">
                            <span>مبلغ واریزی (تومان)</span>
                            {selectedDeal && selectedDeal.remaining_amount > 0 && (
                              <Button 
                                type="link" 
                                size="small" 
                                className="p-0 text-xs text-blue-600 flex items-center gap-1 h-auto"
                                onClick={handleFillRemainingAmount}
                              >
                                <ThunderboltOutlined />
                                کل مانده بدهی ({formatPriceToman(selectedDeal.remaining_amount)})
                              </Button>
                            )}
                          </div>
                        }
                        rules={[{ required: true, message: 'وارد کردن مبلغ واریزی الزامی است' }]}
                      >
                        <InputNumber
                          size="large"
                          className="w-full font-mono text-base font-bold text-emerald-600"
                          placeholder="مبلغ واریزی به تومان"
                          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={value => value.replace(/\$\s?|(,*)/g, '')}
                          onChange={(val) => setEnteredAmount(val)}
                        />
                      </Form.Item>
                      {enteredAmount && (
                        <div className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-lg -mt-3 mb-4 border border-emerald-100 dark:border-emerald-900/50">
                          <span className="text-gray-400 ml-1">به حروف:</span>
                          <span className="font-bold">{amountToPersianWords(enteredAmount)}</span>
                        </div>
                      )}
                    </Col>

                    {/* Payment Method */}
                    <Col xs={24} md={12}>
                      <Form.Item 
                        name="payment_method_type" 
                        label="روش پرداخت" 
                        rules={[{ required: true, message: 'انتخاب روش پرداخت الزامی است' }]}
                      >
                        <Select size="large" placeholder="انتخاب روش پرداخت">
                          <Option value="card_to_card">کارت به کارت</Option>
                          <Option value="bank_transfer">حواله بانکی (پایا / ساتنا)</Option>
                          <Option value="pos">دستگاه کارتخوان (POS)</Option>
                          <Option value="online">درگاه پرداخت اینترنتی</Option>
                          <Option value="cash">نقدی</Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Bank Account */}
                    <Col xs={24} md={12}>
                      <Form.Item 
                        name="bank_account_id" 
                        label="حساب بانکی مقصد (شرکت کارمد)" 
                        rules={[{ required: true, message: 'انتخاب حساب بانکی مقصد الزامی است' }]}
                      >
                        <Select size="large" placeholder="انتخاب حساب بانکی شرکت">
                          {bankAccounts.map(b => (
                            <Option key={b.id} value={b.id}>
                              {b.bank_name || 'بانک'} {b.account_number ? `- ${b.account_number}` : ''}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Purpose */}
                    <Col xs={24} md={12}>
                      <Form.Item 
                        name="payment_purpose" 
                        label="بابت پرداخت" 
                        rules={[{ required: true, message: 'انتخاب بابت واریز الزامی است' }]}
                      >
                        <Select size="large" placeholder="انتخاب بابت واریز">
                          <Option value="down_payment">پیش‌پرداخت معامله</Option>
                          <Option value="installment">قسط معامله</Option>
                          <Option value="invoice">فاکتور</Option>
                          <Option value="settlement">تسویه نهایی معامله</Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Sales Channel */}
                    <Col xs={24} md={12}>
                      <Form.Item 
                        name="sales_channel" 
                        label="کانال فروش" 
                        rules={[{ required: true, message: 'انتخاب کانال فروش الزامی است' }]}
                      >
                        <Select size="large" placeholder="انتخاب کانال فروش">
                          <Option value="phone">تلفنی</Option>
                          <Option value="online">آنلاین / وب‌سایت</Option>
                          <Option value="in_person">حضوری</Option>
                          <Option value="b2b">سازمانی (B2B)</Option>
                        </Select>
                      </Form.Item>
                    </Col>

                    {/* Tracking Code */}
                    <Col xs={24} md={12}>
                      <Form.Item 
                        name="tracking_code" 
                        label="کد پیگیری / شناسه ارجاع بانکی"
                      >
                        <Input 
                          size="large" 
                          placeholder="مثال: شماره ارجاع، کد پیگیری فیش..." 
                          className="font-mono"
                        />
                      </Form.Item>
                    </Col>

                    {/* Paid Date */}
                    <Col xs={24} md={12}>
                      <Form.Item 
                        name="paid_on" 
                        label="تاریخ و ساعت واریز" 
                        rules={[{ required: true, message: 'انتخاب تاریخ واریز الزامی است' }]}
                      >
                        <PersianDatePicker 
                          placeholder="انتخاب تاریخ واریز" 
                          style={{ width: '100%', height: '40px' }} 
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>

                {/* 3. Receipt Upload & Remarks Card */}
                <Card 
                  bordered={false} 
                  className="shadow-sm rounded-2xl mb-6 border border-gray-100 dark:border-gray-800 dark:bg-gray-800"
                  title={
                    <span className="font-bold text-sm text-gray-800 dark:text-gray-100 flex items-center gap-2" style={{ fontFamily: 'CustomFont' }}>
                      <FileImageOutlined className="text-purple-500" />
                      بارگذاری تصویر فیش و توضیحات
                    </span>
                  }
                >
                  <Form.Item label="تصویر فیش واریزی یا سند بانکی (اختیاری)">
                    {fileList.length === 0 ? (
                      <Dragger 
                        beforeUpload={handleBeforeUpload}
                        fileList={fileList}
                        maxCount={1}
                        className="bg-gray-50/60 dark:bg-gray-900/40 border-dashed border-2 border-gray-300 dark:border-gray-700 rounded-xl p-4"
                      >
                        <p className="ant-upload-drag-icon text-3xl text-blue-500 mb-2">
                          <InboxOutlined />
                        </p>
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                          تصویر فیش واریزی را اینجا بکشید یا کلیک کنید
                        </p>
                        <p className="text-xs text-gray-400 m-0">
                          فرمت‌های مجاز: PNG, JPG, JPEG یا PDF (حداکثر ۵ مگابایت)
                        </p>
                      </Dragger>
                    ) : (
                      <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {fileList[0].preview ? (
                            <img 
                              src={fileList[0].preview} 
                              alt="پیش‌نمایش فیش" 
                              className="w-16 h-16 object-cover rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer"
                              onClick={() => handlePreview(fileList[0])}
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-xl">
                              <FileTextOutlined />
                            </div>
                          )}
                          <div>
                            <div className="font-bold text-sm text-gray-800 dark:text-gray-200">{fileList[0].name}</div>
                            <div className="text-xs text-gray-400">
                              {(fileList[0].size / 1024).toFixed(1)} کیلوبایت
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {fileList[0].preview && (
                            <Button 
                              icon={<EyeOutlined />} 
                              size="small"
                              onClick={() => handlePreview(fileList[0])}
                            >
                              مشاهده بزرگنمایی
                            </Button>
                          )}
                          <Button 
                            danger 
                            icon={<DeleteOutlined />} 
                            size="small"
                            onClick={handleRemoveFile}
                          >
                            حذف فایل
                          </Button>
                        </div>
                      </div>
                    )}
                  </Form.Item>


                  {/* قسط بعدی (اختیاری): پس از تایید مالی به بدهی‌ها اضافه می‌شود */}
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="next_installment_amount"
                        label={<span className="font-medium">مبلغ قسط بعدی (تومان - اختیاری)</span>}
                      >
                        <InputNumber
                          style={{ width: '100%', fontFamily: 'CustomFont' }}
                          min={1}
                          formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(val) => val.replace(/\$\s?|(,*)/g, '')}
                          placeholder="مبلغ قسط بعدی"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="next_installment_date"
                        label={<span className="font-medium">تاریخ تقریبی پرداخت قسط بعدی</span>}
                  help="قسط بعدی فقط در صورت پر بودن هر دو فیلد (مبلغ و تاریخ) ثبت می‌شود."
                      >
                        <PersianDatePicker placeholder="انتخاب تاریخ" className="w-full" style={{ height: '40px' }} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="remarks" label="توضیحات و یادداشت تکمیلی">
                    <Input.TextArea 
                      rows={3} 
                      placeholder="توضیحات تکمیلی کارشناس فروش برای مدیر مالی (در صورت وجود)..." 
                    />
                  </Form.Item>
                </Card>

                {/* Submit Action Bar */}
                <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <SafetyCertificateOutlined className="text-emerald-500 text-sm" />
                    <span>پس از ثبت، واریزی مستقیماً در کارتابل تأیید مدیر مالی قرار می‌گیرد.</span>
                  </div>

                  <Space size="middle">
                    <Button 
                      size="large" 
                      onClick={() => {
                        form.resetFields();
                        setSelectedDeal(null);
                        setFileList([]);
                        setEnteredAmount(null);
                      }}
                    >
                      انصراف و پاک‌سازی
                    </Button>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      size="large"
                      loading={loading}
                      icon={<CheckCircleOutlined />}
                      className="bg-emerald-600 hover:bg-emerald-700 font-bold px-6"
                    >
                      ثبت واریزی و ارسال به کارتابل
                    </Button>
                  </Space>
                </div>
              </Form>
            </Col>

            {/* Sidebar Column: Live Deal Financial Summary (Left) */}
            <Col xs={24} lg={9} xl={8}>
              <div className="sticky top-6">
                {selectedDeal ? (
                  <Card 
                    bordered={false} 
                    className="shadow-md rounded-2xl border border-emerald-200/80 dark:border-emerald-900/40 bg-gradient-to-b from-emerald-50/50 via-white to-white dark:from-slate-800 dark:via-slate-800 dark:to-slate-900 overflow-hidden"
                  >
                    {/* Header */}
                    <div className="pb-4 border-b border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-mono text-gray-400">شناسه: #{selectedDeal.id}</span>
                        <Tag 
                          color={FINANCIAL_STATUS_CONFIG[selectedDeal.financial_status]?.color || 'default'} 
                          className="text-xs px-2.5 py-0.5 rounded-full font-bold m-0"
                        >
                          {FINANCIAL_STATUS_CONFIG[selectedDeal.financial_status]?.text || selectedDeal.financial_status}
                        </Tag>
                      </div>
                      <h4 className="text-base font-bold text-gray-800 dark:text-gray-100 m-0 leading-snug">
                        {selectedDeal.name}
                      </h4>
                    </div>

                    {/* Client Info */}
                    <div className="py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <Avatar icon={<UserOutlined />} className="bg-blue-600" />
                        <div>
                          <div className="text-xs text-gray-400">مشتری طرف حساب</div>
                          <div className="text-sm font-bold text-gray-800 dark:text-gray-100">
                            {selectedDeal.client?.name || 'مشتری نامشخص'}
                          </div>
                        </div>
                      </div>
                      {selectedDeal.client?.mobile && (
                        <Tag className="text-xs font-mono px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-0" dir="ltr">
                          <PhoneOutlined className="ml-1" />
                          {selectedDeal.client.mobile}
                        </Tag>
                      )}
                    </div>

                    {/* Progress */}
                    <div className="py-4 border-b border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-gray-500 dark:text-gray-400">درصد تسویه مالی</span>
                        <span className="font-bold text-emerald-600 font-mono">
                          {selectedDeal.settlement_percentage || 0}٪
                        </span>
                      </div>
                      <Progress 
                        percent={selectedDeal.settlement_percentage || 0} 
                        status={selectedDeal.settlement_percentage >= 100 ? 'success' : 'active'} 
                        strokeColor="#10b981"
                        showInfo={false}
                      />
                    </div>

                    {/* 3 Metric Tiles */}
                    <div className="py-4 space-y-2.5 text-xs">
                      <div className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-400">ارزش کل معامله:</span>
                        <span className="font-bold text-sm text-gray-800 dark:text-gray-100 font-mono">
                          {formatPriceToman(selectedDeal.value)}
                        </span>
                      </div>

                      <div className="p-3 bg-emerald-50/60 dark:bg-emerald-950/30 rounded-xl flex items-center justify-between border border-emerald-100 dark:border-emerald-900/40">
                        <span className="text-emerald-700 dark:text-emerald-400">واریز شده قبلی:</span>
                        <span className="font-bold text-sm text-emerald-700 dark:text-emerald-400 font-mono">
                          {formatPriceToman(selectedDeal.paid_amount)}
                        </span>
                      </div>

                      <div className="p-3 bg-red-50/60 dark:bg-red-950/30 rounded-xl flex items-center justify-between border border-red-100 dark:border-red-900/40">
                        <span className="text-red-700 dark:text-red-400 font-bold">مانده بدهی معامله:</span>
                        <span className="font-black text-base text-red-600 dark:text-red-400 font-mono">
                          {formatPriceToman(selectedDeal.remaining_amount)}
                        </span>
                      </div>
                    </div>

                    {/* Installments Checklist */}
                    {selectedDeal.installments && selectedDeal.installments.length > 0 && (
                      <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                        <div className="text-xs font-bold text-gray-700 dark:text-gray-300 mb-2">
                          وضعیت اقساط معامله:
                        </div>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                          {selectedDeal.installments.map(i => (
                            <div 
                              key={i.id} 
                              className="text-[11px] p-2 rounded-lg bg-gray-50 dark:bg-slate-800/80 flex items-center justify-between"
                            >
                              <span>قسط {i.installment_number}</span>
                              <span className="font-mono">{formatPriceToman(i.amount)}</span>
                              <Tag 
                                color={i.status === 'paid' ? 'success' : (i.status === 'overdue' ? 'error' : 'default')} 
                                className="text-[10px] m-0 px-1 py-0"
                              >
                                {i.status === 'paid' ? 'پرداخت‌شده' : (i.status === 'overdue' ? 'معوق' : 'سررسید نشده')}
                              </Tag>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>
                ) : (
                  <Card 
                    bordered={false} 
                    className="shadow-sm rounded-2xl border border-gray-100 dark:border-gray-800 text-center py-8 dark:bg-gray-800"
                  >
                    <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gray-50 dark:bg-gray-700 flex items-center justify-center text-3xl text-gray-400">
                      <DollarCircleOutlined />
                    </div>
                    <div className="font-bold text-sm text-gray-700 dark:text-gray-200 mb-1">
                      معامله‌ای انتخاب نشده است
                    </div>
                    <div className="text-xs text-gray-400 px-4 leading-relaxed mb-4">
                      برای مشاهده ارزش معامله، مانده بدهی و اقساط متصل، معامله مورد نظر را از فرم مقابل انتخاب کنید.
                    </div>
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-xl text-xs text-blue-700 dark:text-blue-300 text-right mx-2 border border-blue-100 dark:border-blue-900/50">
                      <InfoCircleOutlined className="ml-1" />
                      تعداد کل معاملات آماده پرداخت در سامانه: <span className="font-bold">{deals.length} معامله</span>
                    </div>
                  </Card>
                )}
              </div>
            </Col>
          </Row>
        )}

        {/* Modal for full receipt image preview */}
        <Modal
          open={previewVisible}
          footer={null}
          onCancel={() => setPreviewVisible(false)}
          title="پیش‌نمایش تصویر فیش واریزی"
          width={700}
        >
          <img alt="فیش واریزی" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      </div>
    </MainLayout>
  );
};

export default RegisterDealPayment;
